import React, { useEffect, useState, Fragment } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  ThemeProvider,
  createTheme,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import Header from "../../components/Header";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import AuthGuard from "../../components/AuthGuard";
import { useCookies } from "react-cookie";
import { jwtDecode } from "jwt-decode";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import swal from "sweetalert";
import CustomToolbar from "../../components/CustomToolbar";
import CustomToolbarOther from "../../components/CustomToolbarOther";

const themeProvided = createTheme({
  palette: {
    primary: {
      main: "#999",
    },
  },
});

const Statement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [statementsData, setStatementsData] = useState([]);
  const [cookies, setCookie] = useCookies(["token"]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [accountList, setAccountList] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [firstSearch, setFirstSearch] = useState(true);
  const [loading, setLoading] = useState(false);

  const preprocessData = (data) => {
    return data.map((row) => ({
      ...row,
      Amount: row.DEBIT == null ? row.CREDIT : row.DEBIT,
      transactionType: row.DEBIT == null ? "Credit" : "Debit",
    }));
  };

  const [columns, setColumns] = useState([
    // {
    //   field: "sequence",
    //   headerName: "SNo",
    //   flex: 0.5,
    // },
    {
      field: "REFERENCE",
      headerName: "Transaction Reference",
      flex: 1,
    },
    {
      field: "transactionType",
      headerName: "Transaction Type",
      flex: 1,
      renderCell: ({ row }) => {
        const iconColor = row.DEBIT === null ? "green" : "red";
        const icon =
          row.DEBIT === null ? (
            <ArrowUpwardIcon style={{ color: iconColor }} />
          ) : (
            <ArrowDownwardIcon style={{ color: iconColor }} />
          );
        const transactionType = row.DEBIT === null ? "Credit" : "Debit";
        return (
          <Typography color={colors.black[500]} sx={{ ml: "5px" }}>
            {icon} {transactionType}
          </Typography>
        );
      },
    },
    {
      field: "Amount",
      headerName: "Amount",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography color={colors.black[500]} sx={{ ml: "5px" }}>
            {row.Amount}
          </Typography>
        );
      },
    },
    {
      field: "TRN_DT",
      headerName: "Transaction Date",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography color={colors.black[500]} sx={{ ml: "5px" }}>
            {row.TRN_DT}
          </Typography>
        );
      },
    },
    {
      field: "transactiondesc".toUpperCase(),
      headerName: "Description",
      flex: 1,
      // cellClassName: "name-column--cell",
    },
    {
      field: "RUN_BAL",
      headerName: "Running Balance",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography color={colors.black[500]} sx={{ ml: "5px" }}>
            {row.RUN_BAL}
          </Typography>
        );
      },
    },
  ]);

  const fetchAccounts = async () => {
    const decodedToken = jwtDecode(cookies["token"]);
    const jsonData = JSON.parse(decodedToken.userInfo);
    const data = await fetch(
      process.env.REACT_APP_BACKEND_URL +
        "/account/company/" +
        jsonData.companies.id,
      {
        headers: {
          Authorization: "Bearer " + cookies["token"],
        },
      },
    );

    if (data.status !== 200) {
      setCookie("token", null);
      return;
    }

    fetch(
      process.env.REACT_APP_BACKEND_URL +
        "/columns/company/" +
        jsonData.companies.id,
      {
        headers: {
          Authorization: "Bearer " + cookies["token"],
        },
      },
    ).then(async (data) => {
      const jsonData = await data.json();
      setColumns((prev) => [
        ...prev,
        ...jsonData["SelectedColumns"].map((dt) => ({
          field: dt.name,
          headerName: dt.name,
          flex: 1,
          renderCell: ({ row }) => {
            return (
              <Typography
                color={colors.black[500]}
                sx={{ ml: "5px" }}
                key={dt.name}
              >
                {row["CUSTOM_" + dt.name]}
              </Typography>
            );
          },
        })),
      ]);
    });

    const json = await data.json();
    setAccountList(json);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAccountChange = (e) => {
    setSelectedAccount(e.target.value);
  };

  const fetchStatement = async () => {
    setFirstSearch(true);
    setStatementsData([]);
    if (selectedAccount === "") {
      swal("Error", "Please Select Account Number", "error");
      return;
    }
    const requestData = {
      accountNumber: selectedAccount,
      fromDate: fromDate,
      toDate: toDate,
    };
    setLoading(true);
    const res = await fetch(
      process.env.REACT_APP_BACKEND_URL + "/transaction/detail",
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + cookies["token"],
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      },
    );
    if (res.status === 401) {
      setCookie("token", null);
      setLoading(false);
      return;
    }
    if (res.status === 404) {
      setStatementsData([]);
      setFirstSearch(false);
      setLoading(false);
      return;
    }
    if (!res.ok) {
      setLoading(false);
      swal("Error", "Server error", "error");
      return;
    }

    const json = await res.json();
    // backend may return [{ message: "data not found" }]
    if (Array.isArray(json) && json.length === 1 && json[0].message) {
      setStatementsData([]);
    } else {
      setStatementsData(preprocessData(json));
    }
    setFirstSearch(false);
    setLoading(false);
  };
  //   if (data.status !== 200) {
  //     setCookie("token", null);
  //     return;
  //   }
  //   const json = await data.json();
  //   setLoading(false);
  //   setFirstSearch(false);
  //   setStatementsData(preprocessData(json));
  // };

  const getRowId = (row) => {
    return row.AC_ENTRY_SR_NO;
  };

  return (
    <AuthGuard>
      <Box m="10px">
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Header subtitle="View Statement" />
        </Box>
        <Box
          m="40px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-row:nth-child(odd)": {
              border: "1px solid white",
              backgroundColor:
                theme.palette.mode === "light" ? colors.white[300] : "#F3F5F8",
            },
            "& .MuiDataGrid-iconSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-root": {
              border: "1px solid white",
            },
            "& .MuiDataGrid-row": {
              border: "1px solid white",
            },
            "& .MuiDataGrid-cell": {
              fontSize: 15,
              color: colors.black[500],
              border: "none !important",
            },
            "& .name-column--cell": {
              fontWeight: "bold",
              color: colors.black[500],
            },
            "& .MuiDataGrid-overlay, css-c1gd0l-MuiDataGrid-overlay": {
              backgroundImage: "url(../../assets/zamzamlogoBlack.png)",
              mt: "70px",
              backgroundSize: "80%",
              opacity: 0.2,
              backgroundRepeat: "no-repeat",
              color: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.white[500],
              color: colors.black[900],
              fontSize: 20,
              border: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "normal",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.white[500],
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: colors.white[500],
              border: "none",
            },
            "& .MuiDataGrid-pagination": {
              "& .MuiTablePagination-menuItem": {
                color: "#000000",
              },
            },
            "& .MuiTablePagination-root": {
              color: "#000000",
            },
            "& .MuiSvgIcon-root ": {
              color: "#000000",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              left: "75%",
              transform: `translateX(-75%)`,
              color: `${colors.black[500]} !important`,
            },
            "& .MuiCheckbox-root": {
              color: `${colors.black[500]} !important`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "none",
            },
          }}
        >
          <ThemeProvider theme={themeProvided}>
            <Box display={"flex"} alignItems={"center"} pr={"3rem"}>
              <div>
                <FormControl sx={{ mr: "2rem", height: "36px" }}>
                  <InputLabel sx={{ top: "-6px" }}>Account</InputLabel>
                  <Select
                    value={selectedAccount}
                    label="Account"
                    onChange={handleAccountChange}
                    sx={{ height: "36px", paddingTop: "6px", width: "13rem" }}
                  >
                    {accountList.map((item, index) => (
                      <MenuItem key={index} value={item.account_number}>
                        {item.account_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    height: "36px",
                    mr: "2rem",
                    input: { height: "16px", padding: "10px 14px" },
                  }}
                  inputProps={{
                    max: toDate
                      ? new Date(toDate).toISOString().slice(0, 10)
                      : null,
                  }}
                />
                <TextField
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    height: "36px",
                    mr: "2rem",
                    input: { height: "16px", padding: "10px 14px" },
                  }}
                  inputProps={{
                    min: fromDate
                      ? new Date(fromDate).toISOString().slice(0, 10)
                      : null,
                  }}
                />
              </div>
              <Button
                variant="contained"
                onClick={fetchStatement}
                sx={{
                  height: "36px",
                  backgroundColor: "#008083",
                  color: "#fff",
                }}
              >
                Filter Data
              </Button>
            </Box>
          </ThemeProvider>
          <DataGrid
            style={{ position: "relative", height: "100%", width: "100%" }}
            rows={statementsData}
            columns={columns}
            components={{
              Toolbar: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", sm: "row" },
                    alignItems: "center",
                    justifyContent: "right",
                    width: "100%",
                    flexWrap: "wrap",
                  }}
                >
                  <CustomToolbarOther />
                  <Box
                    sx={{
                      minWidth: "300px",
                      marginLeft: "-50px",
                      flexDirection: { xs: "row", sm: "row" },
                    }}
                  >
                    <CustomToolbar
                      rows={statementsData}
                      columns={columns}
                      accountNumber={selectedAccount}
                      date_from={fromDate}
                      date_to={toDate}
                      style={{ flex: "1 1 0%" }}
                    />
                  </Box>
                </Box>
              ),
            }}
            getRowId={getRowId}
          />
          {!firstSearch && statementsData.length === 0 && (
            <Typography
              variant="h1"
              sx={{
                position: "fixed",
                left: "45%",
                bottom: "50%",
                color: "red",
                opacity: 0.3,
                zIndex: 1000,
              }}
            >
              No Data Available.
            </Typography>
          )}
          {loading && (
            <CircularProgress
              sx={{
                position: "fixed",
                left: "50%",
                bottom: "50%",
                color: colors.zamzamBrandGrey[500],
              }}
            />
          )}
        </Box>
      </Box>
    </AuthGuard>
  );
};

export default Statement;
